import { Rocket, Zap } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

interface Props {
  orgSlug: string;
  projectSlug: string;
  prodBranch: string | null;
}

/**
 * v4 (004-project-overview-tab, US5 / Phase 7) — empty state shown on the
 * Overview tab when a project has no active production deployment yet.
 *
 * Two states:
 * - prodBranch set → "Push a commit to <prodBranch>" + secondary CTA to
 *   create a deploy hook.
 * - prodBranch null → "Configure your production branch in Settings" with
 *   a settings link instead.
 */
export function EmptyState({ orgSlug, projectSlug, prodBranch }: Props) {
  const projectBase = `/orgs/${orgSlug}/projects/${projectSlug}`;

  return (
    <div className="of-card p-10 text-center">
      <div className="mx-auto mb-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent-subtle text-accent">
        <Rocket className="h-5 w-5" />
      </div>
      <div className="text-base font-semibold">No production deployment yet</div>
      {prodBranch ? (
        <>
          <div className="text-xs text-muted-foreground mt-2">
            Push a commit to <span className="font-mono">{prodBranch}</span> to deploy.
          </div>
          <div className="mt-5 flex items-center justify-center gap-2">
            <Button asChild variant="default" size="sm">
              <Link to={`${projectBase}/deploy-hooks`}>
                <Zap className="h-3.5 w-3.5" />
                Create a deploy hook
              </Link>
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="text-xs text-muted-foreground mt-2">
            Configure your production branch in Settings to enable auto-deploys.
          </div>
          <div className="mt-5 flex items-center justify-center gap-2">
            <Button asChild variant="default" size="sm">
              <Link to={`${projectBase}/settings`}>Open Settings</Link>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
